import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import { callAdmin } from '@/integrations/supabase/client';
import {
    Building, Users, Mic, MoreVertical, Search,
    AlertTriangle, CreditCard
} from 'lucide-react';

interface Facility {
    id: string;
    name: string;
    type: 'facility' | 'family' | 'school' | 'family_edu' | 'homeschool' | 'church' | string;
    stripe_customer_id: string | null;
    stripe_subscription_id: string | null;
    total_seats: number;
    active_seats: number;
    voice_enabled: boolean;
    created_at: string;
    updated_at: string;
}

interface FacilityManagementProps {
    typeFilter?: 'elderly' | 'education' | 'all';
}

export const FacilityManagement: React.FC<FacilityManagementProps> = ({ typeFilter = 'all' }) => {
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
    const [updating, setUpdating] = useState(false);

    const fetchFacilities = useCallback(async () => {
        try {
            setLoading(true);
            const result = await callAdmin('get_facilities', {});
            if (result.success) {
                setFacilities((result.data as Facility[]) || []);
            } else {
                setError(result.error || 'Failed to fetch facilities');
            }
        } catch (err) {
            setError('Failed to fetch facilities');
            console.error('Error fetching facilities:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFacilities();
    }, [fetchFacilities]);

    const handleUpdateFacility = async (id: string, updates: Partial<Facility>) => {
        try {
            setUpdating(true);
            const result = await callAdmin('update_facility', { id, ...updates });
            if (result.success) {
                setFacilities(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
                setEditingFacility(null);
            } else {
                alert(result.error || 'Failed to update facility');
            }
        } catch (err) {
            console.error('Error updating facility:', err);
            alert('An error occurred while updating the facility');
        } finally {
            setUpdating(false);
        }
    };

    const filteredFacilities = facilities.filter(f => {
        const matchesSearch = f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            f.id.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        if (typeFilter === 'elderly') {
            return f.type === 'facility' || f.type === 'family';
        }
        if (typeFilter === 'education') {
            return f.type === 'school' || f.type === 'family_edu' || f.type === 'homeschool';
        }
        return true;
    });

    if (loading && facilities.length === 0) {
        return (
            <div className="flex justify-center p-12">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                        placeholder="Search facilities..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <div className="text-sm text-gray-500">
                    Total: {facilities.length} facilities
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredFacilities.map((facility) => (
                    <Card key={`fac-${facility.id}`} className="p-6 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${(facility.type === 'family' || facility.type === 'family_edu') ? 'bg-pink-100' :
                                    facility.type === 'homeschool' ? 'bg-amber-100' :
                                    facility.type === 'church' ? 'bg-indigo-100' :
                                    (facility.type === 'school') ? 'bg-blue-100' : 'bg-emerald-100'
                                    }`}>
                                    {(facility.type === 'family' || facility.type === 'family_edu' || facility.type === 'homeschool') ? (
                                        <Users className={`w-5 h-5 ${
                                            facility.type === 'family_edu' ? 'text-purple-600' :
                                            facility.type === 'homeschool' ? 'text-amber-600' : 'text-pink-600'
                                        }`} />
                                    ) : (
                                        <Building className={`w-5 h-5 ${facility.type === 'school' ? 'text-blue-600' : facility.type === 'church' ? 'text-indigo-600' : 'text-emerald-400'}`} />
                                    )}
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 line-clamp-1">{facility.name}</h3>
                                    <p className="text-xs text-gray-500 font-mono flex items-center gap-1.5 flex-wrap">
                                        {facility.id.substring(0, 8)}...
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${(facility.type === 'family' || facility.type === 'family_edu') ? 'bg-pink-50 text-pink-600' :
                                            facility.type === 'homeschool' ? 'bg-amber-50 text-amber-700' :
                                            facility.type === 'church' ? 'bg-indigo-50 text-indigo-700' :
                                            (facility.type === 'school') ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-400'
                                            }`}>
                                            {facility.type.replace('_', ' ')}
                                        </span>
                                        {facility.type === 'homeschool' && (
                                            facility.stripe_subscription_id ? (
                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-50 text-emerald-300 border border-emerald-200">
                                                    Subscribed
                                                </span>
                                            ) : (
                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200">
                                                    Shell / Trial
                                                </span>
                                            )
                                        )}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingFacility(facility)}
                                className="p-2 h-auto"
                            >
                                <MoreVertical className="w-4 h-4" />
                            </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                                    <Users className="w-3 h-3" /> Seats
                                </p>
                                <p className="text-lg font-bold">
                                    {facility.active_seats} / {facility.total_seats}
                                </p>
                            </div>
                            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                                    <Mic className="w-3 h-3" /> Voice
                                </p>
                                <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${facility.voice_enabled ? 'bg-purple-500' : 'bg-gray-300'}`}></span>
                                    <p className="text-sm font-medium">{facility.voice_enabled ? 'Enabled' : 'Disabled'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <Button
                                className="w-full bg-slate-900 hover:bg-slate-800 text-white text-xs h-9 rounded-xl"
                                onClick={() => {
                                    const path = (facility.type === 'family' || facility.type === 'family_edu') ? '/family-admin' :
                                        facility.type === 'homeschool' ? '/homeschool/parent' :
                                        facility.type === 'church' ? '/church/admin' : '/facility-admin';
                                    window.open(`${path}?facility_id=${facility.id}`, '_blank');
                                }}
                            >
                                Inspect {
                                    facility.type === 'school' ? 'School' :
                                        facility.type === 'family_edu' ? 'Learning Hub' :
                                            facility.type === 'homeschool' ? 'Homeschool' :
                                                facility.type === 'church' ? 'Church' :
                                                    facility.type === 'family' ? 'Family' : 'Facility'
                                } Dashboard
                            </Button>
                            <div className="flex items-center justify-between text-[10px] text-gray-400 font-medium px-1">
                                <span className="flex items-center gap-1">
                                    <CreditCard className="w-3 h-3" /> {
                                        facility.stripe_subscription_id ? 'Billing Active' :
                                        facility.stripe_customer_id ? 'Customer (No Sub)' :
                                        facility.type === 'homeschool' ? 'Shell (No Billing)' : 'No Billing'
                                    }
                                </span>
                                <span>Joined {new Date(facility.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {filteredFacilities.length === 0 && (
                <div className="text-center py-12 text-gray-500 bg-white rounded-2xl border border-dashed">
                    No facilities found matching your search.
                </div>
            )}

            {/* Edit Modal */}
            {editingFacility && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="p-6 space-y-4">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-900">Edit Facility</h2>
                                <Button variant="ghost" size="sm" onClick={() => setEditingFacility(null)}>×</Button>
                            </div>

                            <div className="space-y-3">
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">Name</label>
                                    <Input
                                        defaultValue={editingFacility.name}
                                        onChange={(e) => editingFacility.name = e.target.value}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700">Total Seats</label>
                                    <Input
                                        type="number"
                                        defaultValue={editingFacility.total_seats}
                                        onChange={(e) => editingFacility.total_seats = parseInt(e.target.value) || 0}
                                    />
                                </div>
                                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                                    <div>
                                        <p className="font-medium text-sm">Voice Features</p>
                                        <p className="text-xs text-gray-500">Enable OpenAI Realtime for residents</p>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant={editingFacility.voice_enabled ? "default" : "outline"}
                                        className={editingFacility.voice_enabled ? "bg-purple-600 hover:bg-purple-700" : ""}
                                        onClick={() => {
                                            setEditingFacility({ ...editingFacility, voice_enabled: !editingFacility.voice_enabled });
                                        }}
                                    >
                                        {editingFacility.voice_enabled ? 'Enabled' : 'Disabled'}
                                    </Button>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button className="flex-1" variant="outline" onClick={() => setEditingFacility(null)}>Cancel</Button>
                                <Button
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                    disabled={updating}
                                    onClick={() => handleUpdateFacility(editingFacility.id, {
                                        name: editingFacility.name,
                                        total_seats: editingFacility.total_seats,
                                        voice_enabled: editingFacility.voice_enabled
                                    })}
                                >
                                    {updating ? <LoadingSpinner size="sm" /> : 'Save Changes'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
